/*
 * LinkedMeetingNotes — Activity workspace surface for the bidirectional
 * Meeting Note ↔ Activity relationship (Iter 90).
 *
 * The link exists at the data layer already: any meeting/insight note that
 * partners have attached to an activity carries `activity_briefing_id` on
 * itself, and the activity_briefing note is queryable in reverse via the
 * existing `GET /notes?activity_briefing_id=<id>` filter. This component
 * simply surfaces that reverse edge so opening an Activity from its
 * Engagement workspace no longer hides the meetings that captured its
 * substance.
 *
 * Behaviour matches the partner brief:
 *   · shows the note title, meeting date, author, note type
 *   · orders chronologically (newest first — most recent context up top)
 *   · clicking a row opens the same canonical NoteViewerDialog
 *   · empty state is intentional (silent) so activities without linked
 *     notes don't get an accusatory empty box
 *   · removal from the meeting side (clearing activity_briefing_id) makes
 *     the row disappear on the next fetch — no duplicate note is stored
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Note as NoteIcon, ArrowSquareOut } from "@phosphor-icons/react";
import { noteLabel } from "@/lib/noteTypes";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function LinkedMeetingNotes({ activityId, onOpenNote }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    if (!activityId) return;
    setLoading(true);
    api
      .get(`/notes?activity_briefing_id=${activityId}`)
      .then(({ data }) => {
        if (!alive) return;
        // Filter defensively — the endpoint filters server-side but we
        // exclude the activity_briefing itself if it ever slips through,
        // and sort newest first so the most recent capture leads.
        const list = (Array.isArray(data) ? data : (data.notes || []))
          .filter((n) => n.id !== activityId)
          .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        setRows(list);
      })
      .catch(() => setRows([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [activityId]);

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <section data-testid="activity-linked-notes">
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-1.5">
        <NoteIcon size={12} weight="fill" /> Linked meeting notes ({rows.length})
      </div>
      <ul className="space-y-2">
        {rows.map((n) => {
          const kind = noteLabel(n.note_type) || "Note";
          const author = n.author_name || "";
          const when = formatWhen(n.created_at || n.updated_at);
          const preview = (n.body || "").trim().replace(/\s+/g, " ").slice(0, 140);
          return (
            <li
              key={n.id}
              data-testid={`activity-linked-note-${n.id}`}
              className="border-l-2 border-brass/60 pl-3 font-serif text-sm leading-relaxed"
            >
              <button
                type="button"
                onClick={() => onOpenNote?.(n)}
                className="w-full text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                data-testid={`activity-linked-note-${n.id}-open`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold not-italic group-hover:text-primary transition-colors">
                    {n.title || "Untitled note"}
                  </span>
                  <ArrowSquareOut size={12} weight="bold" className="shrink-0 mt-1 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="mt-0.5 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground flex flex-wrap items-center gap-3">
                  <span>{kind}</span>
                  {when && <span>{when}</span>}
                  {author && <span className="normal-case tracking-normal">by {author}</span>}
                </div>
                {preview && (
                  <div className="mt-1 text-muted-foreground/90 line-clamp-2">
                    {preview}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
